import {
  useCreateServiceMutation,
  useUpdateServiceMutation,
} from "@/services/ServiceApis";
import { LoadingButton } from "@/components/ui/loading-button/LoadingButton";
import { Separator } from "@/components/ui/Separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/Sheet";
import { deleteCognitoImage, UploadCognitoImage } from "@/utils/lib/S3Service";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { toast } from "@/components/ui/UseToast";
import { useEffect, useState } from "react";
import { FloatingLabelInput } from "@/components/ui/floating-lable/Floating";
import { MultiSelect } from "@/components/ui/multiselect/MultiSelectCheckBox";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { Status } from "@/app/shared_enums/enums";
import { Button } from "@/components/ui/Button";
import { ErrorType } from "@/app/types";
import { Schedule, Service, Step } from "@/app/types/service";
import { useDeleteImageMutation, useGetDepartmentListQuery, useUploadImageMutation } from "@/services/DepartmentApi";
import { useGetPublishedFormListQuery } from "@/services/FormBuilderApi";
import { Switch } from "@/components/ui/Switch";
import { Label } from "@/components/ui/Label";
import TimeInput from "@/components/custom-component/TimePickerInput";
import { serviceDays } from "@/mock/services";
import { Card } from "@/components/ui/Card";
import { useGetCounterListQuery } from "@/services/CounterApi";
import { ConfirmationDialog } from "@/components/ui/common/ConfirmationDailog";
import { deepCompareObjects, formatTimeString } from "@/utils/Helper";
import { Webcam } from "lucide-react";
import { default as profileimg } from "@/assets/default.jpg";
import { InputTags } from "@/components/ui/input-tags/Tags";
import { CounterDetails } from "@/app/types/counter";
import CounterForm from '@/components/admin/counter-management/component/modal/CounterForm';
import { Textarea } from "@/components/ui/TextArea";
const { VITE_VIEW_S3_URL, VITE_S3_DELETE_URL, IS_PRODUCTION, VITE_S3_URL } =
  import.meta.env;

const status = [
  { value: "active", label: "Active", color: "bg-green-500" },
  { value: "inactive", label: "Inactive", color: "bg-red-500" },
  { value: "pending", label: "Pending", color: "bg-yellow-500" },
];

interface ServiceFormProps {
  isOpen: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  action: "add" | "edit";
  setAction: React.Dispatch<React.SetStateAction<"add" | "edit">>;
  refetch: () => void;
  setData: React.Dispatch<React.SetStateAction<Service | undefined>>;
  data: Service | undefined;
}

type TimeSlot = {
  id: number;
  opening_time: string;
  break_start_time: string;
  break_end_time: string;
  closing_time: string;
  ticket_end_time: string;
};

const initialValues = {
  name: "",
  department_id: "",
  form_id: "",
  average_processing_time: "10", // Default to zero or a meaningful placeholder
  status: Status.Active, // Default to "active" as the initial status
  logo: "",
  code: "",
  tags: [] as string[],
  schedules: [
    {
      day: "",
      opening_time: "",
      break_start_time: "",
      break_end_time: "",
      closing_time: "",
      ticket_end_time: "",
    },
  ],
  is_multi_step: false, // Default to single-step service
  assigned_counters: [],
  steps: [],
};

const defaultSteps: Step[] = [
  { step_number: 1, name: "", assigned_counter: 0 },
];

const ServiceForm = ({
  isOpen,
  setOpen,
  action,
  setAction,
  refetch,
  data,
  setData,
}: ServiceFormProps) => {
  const [applyToAllDays, setApplyToAllDays] = useState(false);
  const [avatar, setAvatar] = useState<string | ArrayBuffer | null>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [uploadImage] = useUploadImageMutation();
  const [deleteImage] = useDeleteImageMutation(); // Add the delete image mutation
  const [isCounterFormOpen, setIsCounterFormOpen] = useState<boolean>(false);
  const [counterData, setCounterData] = useState<CounterDetails | undefined>(undefined);

  const [timeValidationErrors, setTimeValidationErrors] = useState<{
    [dayId: number]: {
      opening_time?: string;
      break_start_time?: string;
      break_end_time?: string;
      closing_time?: string;
      ticket_end_time?: string;
    };
  }>({});

  function parseTime(time: string) {
    return time ? new Date(`1/1/1999 ${time}`) : null;
  }

  const validateTime = (
    dayId: number,
    slotId: number,
    field: string,
    newValue: string
  ) => {
    const slot = timeSlots[dayId]?.find((s) => s.id === slotId);
    if (!slot) return;

    const errors = { ...(timeValidationErrors[dayId] || {}) };

    // Clear previous error for this field
    delete errors[field as keyof typeof errors];

    const opening = parseTime(slot.opening_time);
    const closing = parseTime(slot.closing_time);
    const breakStart = parseTime(slot.break_start_time);
    const breakEnd = parseTime(slot.break_end_time);
    const ticketEnd = parseTime(slot.ticket_end_time);

    // Add new field's value for validation
    const newTimeValue = parseTime(newValue);
    const currentSlot = { ...slot, [field]: newValue };

    // Validate ticket end time
    if (field === "ticket_end_time" && newTimeValue) {
      if (opening && newTimeValue <= opening) {
        errors.ticket_end_time = "Ticket end time must be after opening time";
      }
      if (closing && newTimeValue >= closing) {
        errors.ticket_end_time = "Ticket end time must be before closing time";
      }
    }

    // Validate closing time
    if (field === "closing_time" && newTimeValue) {
      if (opening && newTimeValue <= opening) {
        errors.closing_time = "Closing time must be after opening time";
      }
      if (ticketEnd && newTimeValue <= ticketEnd) {
        errors.closing_time = "Closing time must be after ticket end time";
      }
    }

    // Validate break times if present
    if (
      (field === "break_start_time" || field === "break_end_time") &&
      breakStart &&
      breakEnd &&
      breakStart >= breakEnd
    ) {
      errors.break_end_time = "Break end time must be after break start time";
    }

    // Update the errors state
    setTimeValidationErrors((prev) => {
      const updatedErrors = { ...prev };
      if (Object.keys(errors).length) {
        updatedErrors[dayId] = errors;
      } else {
        delete updatedErrors[dayId];
      }
      return updatedErrors;
    });
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const validTypes = ["image/png", "image/jpg", "image/jpeg"];
    const maxSizeMB = 1;
    const maxSizeBytes = maxSizeMB * 1024 * 1024;

    if (file) {
      if (!validTypes.includes(file.type)) {
        toast({
          variant: "destructive",
          title: "Error Uploading Image",
          description:
            "Unsupported image type. Only PNG, JPG, and JPEG are supported.",
        });
        // Reset file input after error
        event.target.value = "";
        return;
      }

      if (file.size > maxSizeBytes) {
        toast({
          variant: "destructive",
          title: "Error Uploading Image",
          description: `Image size exceeds ${maxSizeMB} MB limit.`,
        });
        // Reset file input after error
        event.target.value = "";
        return;
      }

      const reader = new FileReader();

      reader.onload = (e) => {
        const img = new Image();
        img.src = e.target?.result as string;
        img.onload = () => {
          setSelectedImage(file);
          if (e.target?.result !== undefined) {
            setAvatar(e.target.result);
          }
        };
      };

      reader.readAsDataURL(file);
    }
  };

  const handleSetAvatarClick = () => {
    document.getElementById("fileInput")?.click();
  };
  const { data: departmentOptions } = useGetDepartmentListQuery();
  const { data: formOptions } = useGetPublishedFormListQuery();
  const { data: counterOptions, refetch: countersRefetch } =
    useGetCounterListQuery({
      query: `assignment=unassigned${action == "edit" ? `&service_id=${data?.id}` : ""}`,
    });

  const [createService] = useCreateServiceMutation();
  const [updateService] = useUpdateServiceMutation();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const openDialog = () => {
    const { flag, differences } = deepCompareObjects(watcher, initialValues);
    if (!flag) {
      setIsDialogOpen(true);
    } else {
      handleClose();
    }
  };
  const closeDialog = () => setIsDialogOpen(false);

  const form = useForm<Service>({
    mode: "all",
    defaultValues: initialValues,
  });

  const {
    control,
    watch,
    register,
    handleSubmit,
    clearErrors,
    reset,
    setValue,
    formState: { isSubmitting, errors },
  } = form;
  const watcher = watch();

  // select time slots

  const [selectedDays, setSelectedDays] = useState<number[]>([]);
  const [timeSlots, setTimeSlots] = useState<{ [key: number]: TimeSlot[] }>({});

  // multi select
  const [multiSteps, setMultiSteps] = useState<Step[]>(defaultSteps);
  const addStep = () => {
    const newStep: Step = {
      step_number: multiSteps.length + 1,
      name: "",
      assigned_counter: "",
    };
    setMultiSteps((prevSteps) => [...prevSteps, newStep]);
    setValue("steps", [...multiSteps, newStep]);
  };

  const removeStep = (stepNumber: number) => {
    const updatedSteps = multiSteps
      .filter((step) => step.step_number !== stepNumber)
      .map((step, index) => ({ ...step, step_number: index + 1 }));
    setMultiSteps(updatedSteps);
    setValue("steps", updatedSteps);
  };

  const toggleDaySelection = (dayId: number, dayName: string) => {
    setSelectedDays((prev) => {
      const isSelected = prev.includes(dayId);
      const updatedDays = isSelected
        ? prev.filter((id) => id !== dayId)
        : [...prev, dayId];

      if (!isSelected) {
        setTimeSlots((prevSlots) => ({
          ...prevSlots,
          [dayId]: [
            {
              id: Date.now(), // Unique ID for the time slot
              opening_time: "",
              break_start_time: "",
              break_end_time: "",
              closing_time: "",
              ticket_end_time: "",
            },
          ],
        }));
      } else {
        setTimeSlots((prevSlots) => {
          const updatedSlots = { ...prevSlots };
          delete updatedSlots[dayId];
          // remove the unselected slot from payload
          const updatedSchedule = watcher.schedules.filter(
            (timeSlot) => timeSlot.day != dayName
          );
          setValue("schedules", updatedSchedule);
          return updatedSlots;
        });
      }

      // Sort based on the index of the day in the serviceDays array
      const sortedDays = updatedDays.sort((a, b) => {
        const indexA = serviceDays.findIndex((day) => day.id === a);
        const indexB = serviceDays.findIndex((day) => day.id === b);
        return indexA - indexB;
      });
      return sortedDays;
    });
  };

  useEffect(() => {
    if (action === "edit") {
      countersRefetch();
      const payload = { ...data };
      payload.tags = payload.tags || [];

      if (payload?.is_multi_step) {
        const existingSteps = payload.steps;
        setMultiSteps(existingSteps as Step[]);
      }

      let daysArray = payload?.schedules
        ?.map((item) => serviceDays.find((day) => day.day === item.day)?.id)
        .filter((id): id is number => id !== null);

      // ✅ Sort the days based on the index in serviceDays
      daysArray = daysArray?.sort((a, b) => {
        const indexA = serviceDays.findIndex((day) => day.id === a);
        const indexB = serviceDays.findIndex((day) => day.id === b);
        return indexA - indexB;
      });

      const dayObjects = daysArray?.reduce<{ [key: number]: TimeSlot[] }>(
        (acc, id) => {
          const inputItem = payload?.schedules?.find(
            (item) => serviceDays.find((day) => day.id === id)?.day === item.day
          );

          if (inputItem) {
            acc[id] = [
              {
                id,
                opening_time: inputItem.opening_time,
                break_start_time: inputItem.break_start_time ?? "",
                break_end_time: inputItem.break_end_time ?? "",
                closing_time: inputItem.closing_time,
                ticket_end_time: inputItem.ticket_end_time,
              },
            ];
          }
          return acc;
        },
        {}
      );

      setSelectedDays(daysArray || []);
      setTimeSlots(dayObjects || {});
      reset(payload);
      if (payload.logo) {
        setAvatar(VITE_VIEW_S3_URL + payload.logo);
      }
    } else {
      reset(initialValues, { keepIsSubmitted: false, keepSubmitCount: false });
      setMultiSteps(defaultSteps);
    }
  }, [action, reset, data]);

  // Effect to sync `timeSlots` with form schedules
  useEffect(() => {
    if (!Object.keys(timeSlots).length) return;

    const schedules = selectedDays.map((dayId) => {
      const day = serviceDays.find((day) => day.id === dayId)?.day;

      const slots = timeSlots[dayId]?.map((slot) => ({
        opening_time: formatTimeString(slot.opening_time),
        break_start_time: formatTimeString(slot.break_start_time),
        break_end_time: formatTimeString(slot.break_end_time),
        closing_time: formatTimeString(slot.closing_time),
        ticket_end_time: formatTimeString(slot.ticket_end_time),
      }));

      return { day, ...slots[0] };
    });

    setValue("schedules", schedules as Schedule[]);
  }, [selectedDays, timeSlots, setValue, serviceDays]);

  const onError = () => {
    toast({
      variant: "destructive",
      description: "Please fill all the mandatory fields",
    });
  };

  const handleConfirm = async () => {
    closeDialog();
    handleClose();
  };

  const handleRemoveImageClick = () => {
    setSelectedImage(null);
    setAvatar(null);
    setValue("logo", "");
    const fileInput = document.getElementById("fileInput") as HTMLInputElement;
    if (fileInput) {
      fileInput.value = "";
    }
  };
  const handleOpen = () => {
    setAction("add");
    setIsCounterFormOpen(true);
  };
  const onSubmit = async (payload: Service) => {
    // const hasTimeErrors = Object.values(timeValidationErrors).some(
    //   (dayErrors) => dayErrors && Object.keys(dayErrors).length > 0
    // );

    // if (hasTimeErrors) {
    //   toast({
    //     variant: "destructive",
    //     title: "Please fix time validation errors before submitting",
    //   });
    //   return;
    // }
    if (payload.is_multi_step) {
      if (!payload.steps || payload.steps.length <= 1) {
        toast({
          variant: "destructive",
          title: "Please add at least two steps",
        });
        return; // Exit the function
      }

      // Validate each step
      for (const step of payload.steps) {
        if (!step.name || !step.assigned_counter) {
          toast({
            variant: "destructive",
            title:
              "Please ensure all steps have a name and an assigned counter.",
          });
          return; // Exit the function
        }
      }

      delete payload.assigned_counters;
    }

    if (!payload.is_multi_step) {
      if (payload.assigned_counters && payload.assigned_counters.length <= 0) {
        toast({
          variant: "destructive",
          title: "Please add at least one counter",
        });
        return; // Exit the function
      }

      delete payload.steps;
    }

    function parseTime(time: string) {
      return new Date(`1/1/1999 ${time}`);
    }

    if (payload.schedules.length == 0) {
      toast({
        variant: "destructive",
        title: `Please select at least one day `,
      });
      return;
    }

    if (!payload.max_tickets) {
      delete payload.max_tickets;
    }

    for (const day of payload.schedules) {
      const {
        day: dayName,
        opening_time,
        closing_time,
        ticket_end_time,
        break_start_time,
        break_end_time,
      } = day;

      if (dayName == "") {
        toast({
          variant: "destructive",
          title: `Please select at least one day `,
        });
        return;
      }

      // Check for required fields
      const requiredFields = { opening_time, closing_time, ticket_end_time };
      const missingField = Object.entries(requiredFields).find(
        ([key, value]) => !value
      );

      if (missingField) {
        toast({
          variant: "destructive",
          title: `${dayName}: Please ensure all required times (${Object.keys(requiredFields).join(", ")}) are filled.`,
        });
        return; // Exit the onSubmit function
      }

      // Validate opening and closing times
      if (parseTime(opening_time) >= parseTime(closing_time)) {
        toast({
          variant: "destructive",
          title: `${dayName}: Closing time must be later than opening time.`,
        });
        return;
      }

      // Validate ticket end time
      if (
        parseTime(ticket_end_time) <= parseTime(opening_time) ||
        parseTime(ticket_end_time) >= parseTime(closing_time)
      ) {
        toast({
          variant: "destructive",
          title: `${dayName}: Ticket end time must be between opening time and closing time.`,
        });
        return;
      }

      // Validate break times
      if (break_start_time === "") {
        delete day.break_start_time;
      }

      if (break_end_time === "") {
        delete day.break_end_time;
      }

      if (
        break_end_time &&
        break_start_time &&
        break_start_time === break_end_time
      ) {
        toast({
          variant: "destructive",
          title: `${dayName}: Break start time and end time cannot be the same.`,
        });
        return;
      } else {
        // Ensure break times are valid
        if (
          break_start_time &&
          parseTime(break_start_time) <= parseTime(opening_time)
        ) {
          toast({
            variant: "destructive",
            title: `${dayName}: Break start time must be later than opening time.`,
          });
          return;
        }

        if (
          break_end_time &&
          break_start_time &&
          parseTime(break_end_time) <= parseTime(break_start_time)
        ) {
          toast({
            variant: "destructive",
            title: `${dayName}: Break end time must be later than break start time.`,
          });
          return;
        }

        if (
          break_end_time &&
          parseTime(break_end_time) >= parseTime(closing_time)
        ) {
          toast({
            variant: "destructive",
            title: `${dayName}: Break end time must be earlier than closing time.`,
          });
          return;
        }

        if (
          break_start_time &&
          parseTime(break_start_time) >= parseTime(ticket_end_time)
        ) {
          toast({
            variant: "destructive",
            title: `${dayName}: Break start time must be earlier than ticket end time.`,
          });
          return;
        }
      }
    }

    try {
      // Handle image logic
      if (selectedImage) {
        if (action === "edit" && data?.logo) {
          try {
            await deleteCognitoImage(data?.logo);
          } catch (deleteError) {
            console.error("Failed to delete previous image:", deleteError);
          }
        }
        try {
          const getUrl = await UploadCognitoImage(selectedImage);
          payload.logo = getUrl.location as string;
        } catch (uploadError) {
          payload.logo = "";
          console.error("Failed to upload new image:", uploadError);
        }
      } else if (action === "edit" && data?.logo && avatar !== null) {
        // Only keep the existing logo if it wasn't removed
        payload.logo = avatar === null && !selectedImage ? "" : data.logo;
      } else {
        // If image was removed or no image was selected
        payload.logo = "";
      }

      if (action === "add") {
        const resp = await createService({
          ...payload,
          code: payload.code,
        })
          .unwrap()
          .then((res) => {
            toast({
              variant: "success",
              title: "Service created successfully",
            });
            refetch();
            handleClose();
            countersRefetch();
          })
          .catch((err) => {
            toast({
              variant: "destructive",
              title: `${err?.data?.message || err?.data?.detail}`,
            });
          });
      } else if (action === "edit") {
        const resp = await updateService({
          ...payload,
          code: payload.code,
        })
          .unwrap()
          .then((res) => {
            toast({
              variant: "success",
              title: "Service updated successfully",
            });
            refetch();
            countersRefetch();
            handleClose();
          })
          .catch((err) => {
            toast({
              variant: "destructive",
              title: `${err?.data?.detail || err?.data?.message}`,
            });
          });
      }
      countersRefetch();
    } catch (error: unknown) {
      console.error("Error", { error });
      const errorMessage =
        error && typeof error === "object" && "data" in error
          ? (error as ErrorType).data?.message ||
          (error as ErrorType).data?.detail
          : "Something Went Wrong.";
      toast({
        variant: "destructive",
        title: "Error in form Submission",
        description: errorMessage,
      });
    }
  };

  const handleClose = () => {
    clearErrors();
    reset({ ...initialValues, tags: [] as string[] });
    setAvatar(null);
    setSelectedImage(null);
    setAction("add");
    setData(undefined);
    setTimeSlots({});
    setSelectedDays([]);
    setOpen(false);
    setTimeValidationErrors({});
  };
  // Add this function to your component
  const handleTimeChange = (
    dayId: number,
    slotId: number,
    field: string,
    newValue: string
  ) => {
    setTimeSlots((prevSlots) => {
      const updatedSlots = { ...prevSlots };

      // Update the current day
      updatedSlots[dayId] = prevSlots[dayId].map((s) =>
        s.id === slotId ? { ...s, [field]: newValue } : s
      );

      // If apply to all days is enabled, update all other selected days
      if (applyToAllDays) {
        selectedDays.forEach((day) => {
          if (day !== dayId && updatedSlots[day]) {
            updatedSlots[day] = updatedSlots[day].map((s) => ({
              ...s,
              [field]: newValue,
            }));
          }
        });
      }

      return updatedSlots;
    });

    validateTime(dayId, slotId, field, newValue);
  };


  return (
    <Sheet open={isOpen} onOpenChange={() => setOpen(false)}>
      <SheetContent
        hideCloseButton
        className="!max-w-[1050px] p-0 custom-scrollbar  sm:w-[90%] sm:max-w-2xl "
      >
        <FormProvider {...form}>
          <form
            onSubmit={handleSubmit(onSubmit, onError)}
            className="flex flex-col  gap-4 bg-gray-100 h-screen"
            noValidate
          >
            <SheetHeader className="sticky top-0 z-40 px-4 py-3 bg-white border-2 border-transparent border-b-gray-200">
              <SheetTitle>
                <div className="flex justify-between gap-5 items-center  bg-white">
                  <p className="font-semibold">
                    {action === "add" ? "Create" : "Edit"} Service
                  </p>

                  <div className="flex gap-2">
                    <Button
                      type={"button"}
                      onClick={openDialog}
                      className="!cursor-pointer w-[100px] gap-2  bg-transparent border border-primary text-black hover:border-primary hover:bg-muted"
                    >
                      <i className="fa-regular fa-x  text-xs font-semibold "></i>
                      Cancel
                    </Button>
                    <LoadingButton
                      type="submit"
                      loading={isSubmitting}
                      disabled={isSubmitting}
                    >
                      {!isSubmitting && (
                        <i className="fa-regular fa-floppy-disk text-base px-1 "></i>
                      )}
                      {action == "edit" ? "Update" : "Save"}
                    </LoadingButton>
                  </div>
                </div>
              </SheetTitle>
            </SheetHeader>
            <SheetDescription className="bg-gray-100">
              <Card className="p-4 mx-4 bg-white rounded-lg">
                <h2 className="font-medium">Service Details</h2>
                <Separator className=" h-[1px] rounded-full my-2" />

                <div className="mb-2 flex justify-start items-center ">
                  <div className="flex flex-row gap-4 items-center just">
                    <div className="flex justify-center items-center ">
                      <img
                        id="avatar"
                        src={
                          avatar
                            ? String(avatar)
                            : watcher.logo
                              ? VITE_VIEW_S3_URL + watcher.logo
                              : profileimg
                        }
                        loading="lazy"
                        alt={profileimg}
                        className="w-24 h-24 rounded-full object-cover relative border-2 border-primary"
                      />
                    </div>
                    <input
                      type="file"
                      id="fileInput"
                      className="hidden"
                      onChange={handleImageChange}
                    />
                    <Button
                      onClick={handleSetAvatarClick}
                      variant={"outline"}
                      type="button"
                      className="px-4 w-60 text-nowrap py-2 bg-transparent gap-2 text-black rounded hover:bg-primary hover:text-gray-500 "
                    >
                      <Webcam className="h-4 w-4" />
                      Select Logo
                    </Button>
                    {(watcher.logo || selectedImage !== null) && (
                      <Button
                        onClick={handleRemoveImageClick}
                        variant={"outline"}
                        type="button"
                        className="px-4 w-fit bg-red-500 text-nowrap py-2 bg-transparent gap-2 text-black rounded hover:bg-primary hover:text-gray-500 "
                      >
                        <i className="fa-regular fa-trash-alt text-red-600"></i>
                      </Button>
                    )}
                  </div>
                </div>
                <div className="relative w-[23.7%]">
                  <FloatingLabelInput
                    id="code"
                    label="Service Code"
                    text="*"
                    placeholder="Add a Unique Code"
                    maxLength={40}
                    className="w-full"
                    {...register("code", {
                      required: "Required",
                      maxLength: {
                        value: 40,
                        message: "Maximum 40 characters allowed",
                      },
                    })}
                    error={errors.code?.message}
                  />
                </div>

                <div className="grid grid-cols-3 slg:grid-cols-4 gap-4 pt-4">
                  <FloatingLabelInput
                    label="Service Name"
                    id="name"
                    text="*"
                    placeholder="Enter Service Name"
                    className="capitalize w-full"
                    error={errors.name?.message}
                    {...register("name", {
                      required: "Required",
                      setValueAs: (value) => value.toLowerCase(),
                      maxLength: {
                        value: 40,
                        message: "Should be 40 characters or less",
                      },
                      pattern: {
                        value: /^[A-Za-z0-9-\s]+$/, // Allows letters, numbers, hyphens, and spaces
                        message:
                          "Only alphabets, hyphen (-) and spaces are allowed",
                      },
                    })}
                  />
                  <div className="relative">
                    <Controller
                      control={control}
                      name="department_id"
                      rules={{ required: "Required" }}
                      render={({
                        field: { onChange, value, onBlur },
                        fieldState: { invalid, error },
                      }) => (
                        <Select
                          onValueChange={(value: string) =>
                            setValue("department_id", Number(value))
                          }
                          value={String(value)}
                        >
                          <SelectTrigger
                            floatingLabel="Department"
                            text="*"
                            className={`text-black w-full capitalize`}
                          >
                            <SelectValue placeholder="Select Department" />
                          </SelectTrigger>
                          <SelectContent className="capitalize max-h-56 custom-scrollbar">
                            {departmentOptions?.map((item, index) => (
                              <SelectItem
                                key={index}
                                value={String(item.value)}
                              >
                                {item.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />

                    {errors.department_id?.message && (
                      <span className="text-destructive font-poppins block !mt-[5px] text-xs">
                        {errors.department_id?.message}
                      </span>
                    )}
                  </div>

                  <FloatingLabelInput
                    label="Average Processing Time"
                    id="average_processing_time"
                    className="w-full"
                    type="number"
                    text="*"
                    error={errors.average_processing_time?.message}
                    placeholder="Enter Avg Time"
                    {...register("average_processing_time", {
                      required: "Required",
                      min: {
                        value: 5,
                        message:
                          "Average Processing Time must be at least 5min",
                      },
                      max: {
                        value: 60,
                        message: "Average Processing Time must be at max 60min",
                      },
                    })}
                  />
                  <FloatingLabelInput
                    label="Max Tickets"
                    id="max_tickets"
                    className="w-full"
                    type="number"
                    text=""
                    error={errors.max_tickets?.message}
                    placeholder="Enter Max Tickets"
                    {...register("max_tickets", {
                      min: {
                        value: 1,
                        message:
                          "Max tickets must be at least 1",
                      },
                    })}
                  />
                  <Controller
                    control={control}
                    name="status"
                    render={({ field }) => (
                      <div className="flex items-center space-x-2">
                        <Switch
                          checked={field.value === Status.Active}
                          onCheckedChange={(checked) =>
                            field.onChange(
                              checked ? Status.Active : Status.Inactive
                            )
                          }
                        />
                        {/* <span>{field.value =='active'?"Active":"Inactive"}</span> */}
                        <span>Status</span>
                      </div>
                    )}
                  />

                  <div className="flex items-center gap-2 col-span-4">
                    <h2 className="text-md flex font-normal text-[#212529]">
                      Days of the Week <span className="text-red-500">*</span> :
                    </h2>
                    <div className="flex gap-2">
                      {serviceDays.map(({ id, day, title }) => (
                        <button
                          key={id}
                          type="button"
                          onClick={() => toggleDaySelection(id, day)}
                          className={`flex h-8 w-8 items-center justify-center rounded-lg text-sm font-medium ${selectedDays.includes(id)
                            ? "bg-primary text-white"
                            : "bg-[#FBF6D5] text-primary"
                            }`}
                        >
                          {title}
                        </button>
                      ))}
                    </div>
                    <div className="flex items-center ml-4">
                      <Switch
                        id="apply-to-all-days"
                        checked={applyToAllDays}
                        onCheckedChange={setApplyToAllDays}
                      />
                      <Label htmlFor="apply-to-all-days" className="ml-2">
                        Apply time to all days
                      </Label>
                    </div>
                  </div>

                  <section className="col-span-4 space-y-4">
                    {selectedDays.map((id) => {
                      const selectedDay = serviceDays.find(
                        (day) => day.id === id
                      )?.day;
                      return (
                        <div key={id} className="flex gap-6 items-end ">
                          <h3 className="text-[.9rem] font-medium text-[#212529] py-3 w-full max-w-24 text-right">{`${selectedDay}`}</h3>
                          {timeSlots[id]?.map((slot) => (
                            <>
                              <div key={slot.id} className="relative my-1">
                                <div className="flex gap-y-3 gap-x-6 flex-wrap items-center">
                                  <div className="flex flex-col">
                                    <TimeInput
                                      label={"Opening Time"}
                                      value={slot.opening_time}
                                      onChange={(e) =>
                                        handleTimeChange(
                                          id,
                                          slot.id,
                                          "opening_time",
                                          e.target.value
                                        )
                                      }
                                      className="input"
                                      placeholder="Start Time"
                                    />
                                  </div>
                                  <div className="flex flex-col">
                                    <TimeInput
                                      label={"Break Start Time"}
                                      value={slot.break_start_time}
                                      onChange={(e) =>
                                        handleTimeChange(
                                          id,
                                          slot.id,
                                          "break_start_time",
                                          e.target.value
                                        )
                                      }
                                      className="input"
                                      placeholder="Break Start"
                                    />
                                  </div>

                                  <div className="flex flex-col">
                                    <TimeInput
                                      label={"Break End Time"}
                                      value={slot.break_end_time}
                                      onChange={(e) =>
                                        handleTimeChange(
                                          id,
                                          slot.id,
                                          "break_end_time",
                                          e.target.value
                                        )
                                      }
                                      className="input"
                                      placeholder="Break End"
                                    />
                                  </div>

                                  <div className="flex flex-col">
                                    <TimeInput
                                      label={"Ticket End Time"}
                                      value={slot.ticket_end_time}
                                      onChange={(e) =>
                                        handleTimeChange(
                                          id,
                                          slot.id,
                                          "ticket_end_time",
                                          e.target.value
                                        )
                                      }
                                      className="input"
                                      placeholder="Ticket End Time"
                                    />
                                  </div>
                                  <div className="flex flex-col">
                                    <TimeInput
                                      label={"Closing Time"}
                                      value={slot.closing_time}
                                      onChange={(e) =>
                                        handleTimeChange(
                                          id,
                                          slot.id,
                                          "closing_time",
                                          e.target.value
                                        )
                                      }
                                      className="input"
                                      placeholder="Closing Time"
                                    />{" "}
                                  </div>
                                </div>
                              </div>
                            </>
                          ))}
                        </div>
                      );
                    })}
                  </section>
                </div>

                <div className="grid grid-cols-2 gap-4 w-full py-2">
                  {/* <div className="w-full">
                    <label className="block text-sm font-semibold">Appointment Form (JSON)</label>
                    <Controller
                      name="form_schema"
                      control={control}
                      render={({ field }) => (
                        <Textarea
                          {...field}
                          placeholder='Paste form JSON schema from Form Builder here (optional)'
                          rows={6}
                        />
                      )}
                    />
                    <p className="text-xs text-muted-foreground">Optional: add a JSON schema produced by the Form Builder to make the appointment form dynamic for this service.</p>
                  </div> */}

                  <Controller
                    control={control}
                    name="form_id"
                    render={({
                      field: { onChange, value, onBlur },
                      fieldState: { invalid, error },
                    }) => (
                      <Select
                        onValueChange={(val: string) =>
                          setValue("form_id", val === "none" ? null : Number(val))
                        }
                        value={value ? String(value) : "none"}
                      >
                        <SelectTrigger
                          floatingLabel="Appointment Form"
                          text=""
                          className={`text-black capitalize `}
                        >
                          <SelectValue placeholder="Select Form" />
                        </SelectTrigger>
                        <SelectContent className="capitalize max-h-56 custom-scrollbar">
                          <SelectItem value="none">-- None --</SelectItem>
                          {formOptions?.map((item, index) => (
                            <SelectItem
                              key={index}
                              value={String(item.value)}
                            >
                              {item.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />

                  <Controller
                    name="tags"
                    control={control}
                    defaultValue={[]}
                    rules={{
                      validate: {
                        maxTags: (value) =>
                          value.length <= 10 || "Maximum 10 tags allowed",
                        uniqueTags: (value) =>
                          new Set(value).size === value.length ||
                          "Duplicate tags are not allowed",
                      },
                    }}
                    render={({ field }) => (
                      <InputTags
                        value={field.value || []}
                        onChange={(newTags) => {
                          field.onChange(newTags);
                        }}
                      />
                    )}
                  />
                  {errors.tags?.message && (
                    <span className="text-destructive font-poppins block !mt-[5px] text-xs">
                      {errors.tags?.message}
                    </span>
                  )}
                </div>
              </Card>

              <Card className="p-4 m-4 bg-white rounded-lg">
                <div className="flex flex-row gap-3 justify-start items-center">
                  <h2 className="font-medium">Counters On Service</h2>
                </div>

                <Separator className=" h-[1px] rounded-full my-2" />
                <div className="grid grid-cols-2 slg:grid-cols-3 gap-4 pt-2">
                  {!watcher.is_multi_step && (
                    <div className="flex flex-row gap-2 col-span-3 items-center justify-start">
                      <div className="relative">
                        <MultiSelect
                          floatingLabel={"Assign Counters"}
                          key={"Assign Counters"}
                          text="*"
                          options={counterOptions || []}
                          defaultValue={watcher.assigned_counters || []} // Ensure defaultValue is always an array
                          onValueChange={(selectedValues) => {
                            setValue(
                              "assigned_counters",
                              selectedValues as number[]
                            ); // Pass selected values to state handler
                          }}
                          placeholder={"Select Counter"}
                          variant="inverted"
                          maxCount={1}
                          className="capitalize border-checkboxborder border-[1px]"
                        />
                        {errors.assigned_counters?.message && (
                          <span className="text-destructive font-poppins block !mt-[5px] text-xs">
                            {errors.assigned_counters?.message}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {watcher.is_multi_step && (
                    <section className="col-span-2">
                      {multiSteps.map((step, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-center gap-2 py-2"
                        >
                          <p className="max-w-16 w-full text-left">{`Step: ${index + 1}`}</p>
                          <FloatingLabelInput
                            placeholder="Enter Step Name"
                            type="text"
                            id={`steps[${index}].name`}
                            value={step.name || ""} // Ensure value is a string
                            onChange={(e) => {
                              const updatedSteps = multiSteps.map((s, i) =>
                                i === index ? { ...s, name: e.target.value } : s
                              );
                              setMultiSteps(updatedSteps);
                              setValue("steps", updatedSteps);
                            }}
                          />

                          <Select
                            value={
                              step.assigned_counter
                                ? String(step.assigned_counter)
                                : ""
                            } // Ensure value is a string or empty
                            onValueChange={(value) => {
                              const updatedSteps = multiSteps.map((s, i) =>
                                i === index
                                  ? { ...s, assigned_counter: Number(value) }
                                  : s
                              );
                              setMultiSteps(updatedSteps);
                              setValue("steps", updatedSteps);
                            }}
                          >
                            <SelectTrigger
                              floatingLabel="Assign Counter"
                              text="*"
                              className="text-black w-full bg-white"
                            >
                              <SelectValue placeholder="Select Counter" />
                            </SelectTrigger>
                            <SelectContent className="capitalize">
                              {counterOptions?.map((item) => (
                                <SelectItem
                                  key={item.value}
                                  value={String(item.value)}
                                >
                                  {item.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="solid"
                              size="icon"
                              className="flex h-[1.2rem] w-[1.2rem] rounded-full bg-transparent border-red-600 border"
                              onClick={() => removeStep(step.step_number)}
                              disabled={multiSteps.length === 1}
                            >
                              <Label className="text-[1.1rem] text-red-600 mt-[.1rem]">
                                -
                              </Label>
                            </Button>
                            <Button
                              variant="solid"
                              size="icon"
                              className="flex h-[1.2rem] w-[1.2rem] rounded-full bg-transparent border-green-600 border"
                              onClick={addStep}
                            >
                              <Label className="text-[1.1rem] text-green-600 mt-[.1rem]">
                                +
                              </Label>
                            </Button>
                          </div>
                        </div>
                      ))}
                    </section>
                  )}
                </div>
              </Card>
            </SheetDescription>

            <ConfirmationDialog
              isOpen={isDialogOpen}
              onClose={closeDialog}
              onConfirm={handleConfirm}
            />
          </form>
        </FormProvider>
      </SheetContent>

      <CounterForm
        isOpen={isCounterFormOpen}
        setOpen={setIsCounterFormOpen}
        action={action}
        setAction={setAction}
        data={counterData}
        setData={setCounterData}
        refetch={countersRefetch}
        disableCloseConfirmation={true}
      />
    </Sheet>
  );
};

export default ServiceForm;
